export type SentimentThemeMode = 'dark' | 'light'

export interface SentimentStageRow {
  min: number
  max: number
  label: string
  signal: string
  signalClass: string
  position: string
  tier: number
}

export const SENTIMENT_STAGES: SentimentStageRow[] = [
  { min: 80, max: 100, label: '亢奋过热', signal: '风险区', signalClass: 'risk', position: '0%~40%', tier: 5 },
  { min: 60, max: 79, label: '活跃强势', signal: '进攻区', signalClass: 'attack', position: '60%~80%', tier: 4 },
  { min: 40, max: 59, label: '中性', signal: '平衡区', signalClass: 'balance', position: '40%~60%', tier: 3 },
  { min: 20, max: 39, label: '低迷谨慎', signal: '观望区', signalClass: 'watch', position: '20%~40%', tier: 2 },
  { min: 0, max: 19, label: '冰点恐慌', signal: '抄底区', signalClass: 'bottom', position: '0%~20%', tier: 1 }
]

export const INDEX_OPTIONS = [
  { key: 'sh', field: 'shChangePct', label: '上证指数' },
  { key: 'sz', field: 'szChangePct', label: '深证成指' },
  { key: 'cyb', field: 'chiNextChangePct', label: '创业板指' }
] as const

export type IndexKey = typeof INDEX_OPTIONS[number]['key']

export const FLOW_RANK_COLORS = ['#FE4043', '#FA8E00', '#E8B339', '#70D084', '#4FA3FF']

export const WS_LIST_KEY = 'SENTIMENT_CAPITAL_FLOW'

export const INDEX_STORAGE_KEY = 'sentiment_index_overlay_v1'

/** 温度计刻度 0–100 对应渐变色（与设计稿一致） */
export function getThermoAccentColor(score: number): string {
  const s = Math.max(0, Math.min(100, score))
  if (s >= 80) return '#f03e41'
  if (s >= 60) return '#ff6505'
  if (s >= 50) return '#fa8e00'
  if (s >= 35) return '#e8b339'
  if (s >= 20) return '#4dbf8b'
  return '#4d8dfc'
}

export const THERMO_GRADIENT_CSS =
  'linear-gradient(to top, #4d8dfc 0%, #4dbf8b 28%, #fa8e00 52%, #ff6505 76%, #f03e41 100%)'

const THERMO_EMPTY_TRACK = {
  dark: require('@/assets/qingxu/thermometer/empty-track.png'),
  light: require('@/assets/qingxu/thermometer/empty-track-light.png')
} as const

export function getThermoEmptyTrackSrc(theme: SentimentThemeMode = 'dark'): string {
  return THERMO_EMPTY_TRACK[theme] || THERMO_EMPTY_TRACK.dark
}

const THERMO_ARROW_BY_TIER: Record<number, string> = {
  1: require('@/assets/qingxu/thermometer/1Arrow.png'),
  2: require('@/assets/qingxu/thermometer/2Arrow.png'),
  3: require('@/assets/qingxu/thermometer/3Arrow.png'),
  4: require('@/assets/qingxu/thermometer/4Arrow.png'),
  5: require('@/assets/qingxu/thermometer/5Arrow.png')
}

export function getThermoArrowSrc(tier: number): string {
  const t = Math.max(1, Math.min(5, Math.round(tier)))
  return THERMO_ARROW_BY_TIER[t] || THERMO_ARROW_BY_TIER[1]
}

export function getStageByScore(score: number): SentimentStageRow {
  const s = roundSentimentScore(score)
  const row = SENTIMENT_STAGES.find((item) => s >= item.min && s <= item.max)
  return row || SENTIMENT_STAGES[SENTIMENT_STAGES.length - 1]
}

/** 情绪值四舍五入取整（0–100） */
export function roundSentimentScore(val: unknown): number {
  if (val == null || Number.isNaN(Number(val))) return 0
  return Math.max(0, Math.min(100, Math.round(Number(val))))
}

/** 情绪值展示文案，无效时为 -- */
export function formatSentimentScore(val: unknown): string {
  if (val == null || Number.isNaN(Number(val))) return '--'
  return String(roundSentimentScore(val))
}

/** 交易日：支持毫秒时间戳或 YYYYMMDD 数字（如 20260527） */
export function parseTradingDay(ts: number): Date {
  if (!ts) return new Date(NaN)
  const n = Number(ts)
  if (!Number.isFinite(n)) return new Date(NaN)
  const intStr = String(Math.floor(n))
  if (intStr.length === 8 && n >= 19000101 && n <= 29991231) {
    const s = intStr
    const y = parseInt(s.slice(0, 4), 10)
    const m = parseInt(s.slice(4, 6), 10) - 1
    const d = parseInt(s.slice(6, 8), 10)
    return new Date(y, m, d)
  }
  return new Date(n)
}

export function formatTradingDay(ts: number): string {
  const d = parseTradingDay(ts)
  if (Number.isNaN(d.getTime())) return '--'
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
}

export function formatMd(ts: number): string {
  const d = parseTradingDay(ts)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getMonth() + 1}/${d.getDate()}`
}

/** 图表横轴：带年份，避免跨年无法区分 */
export function formatChartAxisDate(ts: number): string {
  const d = parseTradingDay(ts)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`
}

/** 涨跌幅展示：红涨绿跌 */
export function pxChangeClass(val: number | null | undefined, theme: SentimentThemeMode): string {
  if (val == null || Number.isNaN(Number(val))) return 'flat'
  const n = Number(val)
  if (n > 0) return 'up'
  if (n < 0) return 'down'
  return 'flat'
}

export function formatPercent(val: number | null | undefined): string {
  if (val == null || Number.isNaN(Number(val))) return '--'
  const n = Number(val)
  const prefix = n > 0 ? '+' : ''
  return `${prefix}${n.toFixed(2)}%`
}

export function assignFlowColors(
  items: { key: string; value: number }[]
): Record<string, string> {
  const sorted = [...items].sort((a, b) => b.value - a.value)
  const total = sorted.length || 1
  const map: Record<string, string> = {}
  sorted.forEach((item, index) => {
    const pct = (index + 1) / total
    let colorIndex = 4
    if (pct <= 0.2) colorIndex = 0
    else if (pct <= 0.4) colorIndex = 1
    else if (pct <= 0.6) colorIndex = 2
    else if (pct <= 0.8) colorIndex = 3
    map[item.key] = FLOW_RANK_COLORS[colorIndex]
  })
  return map
}

export function sectorKey(symbol: string, market: string): string {
  const sym = String(symbol || '').padStart(6, '0')
  let m = (market || '').toLowerCase()
  if (m === 'shetf') m = 'sh'
  if (m === 'szetf') m = 'sz'
  return `${sym}_${m}`
}
